/**
 * Prepends https:// to a bare domain/URL (e.g. "example.com" -> "https://example.com").
 * Leaves URLs that already declare a scheme untouched, including non-http(s)
 * ones — those are left for the backend's SSRF guard to reject with a clear
 * error rather than silently rewritten here.
 */
export const normalizeScanUrl = (input: string): string => {
  const trimmed = input.trim();
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

/**
 * Describes which scan(s) produced a merged finding (a DetectionHit or
 * TagFire's `seenIn`), for UI callouts. Confirmed by both scans is the
 * unremarkable default (null, no callout); one scan only is worth flagging -
 * either the full scan didn't re-confirm something the quick scan already
 * found, or the full scan's richer real-browser evidence found something
 * new. `seenIn` is only ever set by a full-scan merge (see
 * full-scan-merge-utils.ts), so this is also null for a report that's never
 * had one.
 */
export const scanOriginLabel = (seenIn: ('quick' | 'full')[] | undefined): string | null => {
  const set = new Set(seenIn ?? []);
  if (set.has('quick') && set.has('full')) return null;
  if (set.has('full')) return 'full scan only';
  if (set.has('quick')) return 'quick scan only';
  return null;
};

export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

export const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Cross-origin sheets (e.g. Google Fonts) can't be read, so they stay as <link>s.
const collectPageStyles = (): string =>
  Array.from(document.styleSheets)
    .map(sheet => {
      try {
        return `<style>${Array.from(sheet.cssRules, rule => rule.cssText).join('\n')}</style>`;
      } catch {
        return sheet.href ? `<link rel="stylesheet" href="${escapeHtml(sheet.href)}">` : '';
      }
    })
    .join('\n');

// The app shell is a fixed-viewport scroller; a standalone file should just
// flow, so every scroll container is released to its full height. The doubled
// :root outranks the app's own selectors without !important.
const SNAPSHOT_FLOW_OVERRIDES = `<style>
:root:root, :root:root body, :root:root .bpm-page, :root:root .bpm-page > .body,
:root:root .digital-audit-page {
  height: auto; max-height: none; overflow: visible;
}
</style>`;

// cloneNode keeps attributes, not live state, so typed/checked/selected values
// and resolved image URLs are copied onto the clone explicitly. Canvas, video
// and dialog state aren't copied: no Digital Audit view renders them, and the
// client guide iframe is `srcdoc`, which cloneNode already keeps.
const copyLiveState = (source: HTMLElement, clone: HTMLElement): void => {
  const sourceFields = source.querySelectorAll('input, textarea, select, img');
  const cloneFields = clone.querySelectorAll('input, textarea, select, img');
  sourceFields.forEach((field, i) => {
    const target = cloneFields[i];
    if (field instanceof HTMLInputElement && target instanceof HTMLInputElement) {
      target.setAttribute('value', field.value);
      if (field.checked) {
        target.setAttribute('checked', '');
      }
    } else if (field instanceof HTMLTextAreaElement && target instanceof HTMLTextAreaElement) {
      target.textContent = field.value;
    } else if (field instanceof HTMLSelectElement && target instanceof HTMLSelectElement) {
      Array.from(target.options).forEach((option, j) => {
        option.toggleAttribute('selected', field.options[j]?.selected ?? false);
      });
    } else if (field instanceof HTMLImageElement && target instanceof HTMLImageElement) {
      target.setAttribute('src', field.src);
    }
  });
};

/**
 * Renders an element exactly as it's currently shown into a self-contained
 * HTML document. The element's ancestors are re-created as empty wrappers with
 * the same classes, so styles scoped under e.g. `.bpm-page` still apply.
 */
export const renderElementAsHtmlDocument = (element: HTMLElement, title: string): string => {
  const clone = element.cloneNode(true);
  if (!(clone instanceof HTMLElement)) throw new Error('Could not copy the tab for export.');
  copyLiveState(element, clone);

  let html = clone.outerHTML;
  for (let el = element.parentElement; el && el !== document.body; el = el.parentElement) {
    const classAttr = el.className ? ` class="${escapeHtml(el.className)}"` : '';
    html = `<div${classAttr}>${html}</div>`;
  }

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<base href="${window.location.origin}/">
<title>${escapeHtml(title)}</title>
${collectPageStyles()}
${SNAPSHOT_FLOW_OVERRIDES}
</head>
<body class="${escapeHtml(document.body.className)}">${html}</body>
</html>
`;
};
